var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { t } from './translate';
import { v4 as uuid } from 'uuid';
import { DominoBuilderError, DominoBuilderErrorScope, DominoBuilderErrorSeverity, DominoBuilderErrorType, filterErrors, } from './errors';
import { DominoBuilderExpression } from './expressions';
import { getArrayType } from './expressions/helpers';
import { nvpToObject, objectToNVP, performInitialArgumentValueValidation, validateInputs, } from './helpers';
import { isPrimitiveType } from './symbols';
import { HandleType, PrimitiveTypes, } from './types';
export const constructArgumentCollection = (resourceRegistry, parameters, args, handleType, parentHandle, unusedArgumentsAsWarnings = true, genericTypeParameters = undefined) => __awaiter(void 0, void 0, void 0, function* () {
    let argCollection = {
        args: [],
        originalFormat: 'unknown',
        errors: [],
    };
    let providedArgs = [];
    let errors = [];
    // translate argument format if necessary
    if (!!args) {
        if (isArrayArgumentRepresentation(args)) {
            providedArgs = args;
            argCollection.originalFormat = 'name-value';
        }
        else {
            try {
                providedArgs = objectToNVP(args);
                argCollection.originalFormat = 'object';
            }
            catch (e) {
                errors.push(new DominoBuilderError(DominoBuilderErrorType.InvalidArgumentFormat, handleType === HandleType.TriggerContextArgument
                    ? DominoBuilderErrorScope.RUNTIME
                    : DominoBuilderErrorScope.DESIGN, DominoBuilderErrorSeverity.ERROR, 'Arguments are in an unsupported format', {
                    path: `${handleType}::unknown`,
                    handle: parentHandle,
                }));
            }
        }
    }
    yield Promise.all(parameters.map((p) => __awaiter(void 0, void 0, void 0, function* () {
        const arg = yield constructArgument(resourceRegistry, p, providedArgs, handleType, parentHandle);
        return Object.assign(Object.assign({}, arg), { isGenericTyped: !!genericTypeParameters &&
                !!genericTypeParameters.find(genericTypeParam => genericTypeParam.name === p.type) });
    }))).then(constructedArguments => {
        constructedArguments.map(a => {
            argCollection.args.push(a);
        });
    });
    if (unusedArgumentsAsWarnings) {
        errors = providedArgs
            .filter(a => {
            return !parameters.find(p => p.name === a.name);
        })
            .map((a) => {
            return new DominoBuilderError(DominoBuilderErrorType.UnusedArgument, DominoBuilderErrorScope.DESIGN, DominoBuilderErrorSeverity.INFO, `Unused argument specified as: '${[a.name]}'`, {
                path: `${handleType}::${a.name}`,
                handle: parentHandle,
            });
        });
    }
    argCollection.errors = errors;
    argCollection.getErrorAggregate = (scope, severity) => {
        var _a, _b;
        return filterErrors(scope, severity, errors).concat((_b = (_a = argCollection.args) === null || _a === void 0 ? void 0 : _a.reduce((a, c) => { var _a, _b; return a.concat((_b = (_a = c.getErrorAggregate) === null || _a === void 0 ? void 0 : _a.call(c, scope, severity)) !== null && _b !== void 0 ? _b : []); }, [])) !== null && _b !== void 0 ? _b : []);
    };
    return argCollection;
});
export const constructArgument = (resourceRegistry, parameter, providedArgs, handleType, parentHandle) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    // get arg value, if provided
    let value = (_a = providedArgs === null || providedArgs === void 0 ? void 0 : providedArgs.find(a => a.name === parameter.name)) === null || _a === void 0 ? void 0 : _a.value;
    const handle = Object.assign({ id: uuid(), type: handleType }, (!!parentHandle ? { parent: parentHandle } : {}));
    const { errors, validatedValue } = performInitialArgumentValueValidation(handle, value, parameter);
    value =
        handleType === HandleType.TriggerGenericTypeArgument
            ? validateInputs(validatedValue)
            : validatedValue;
    value = yield convertValueToExpression(handle, resourceRegistry, value, parameter.name, parameter.type, errors);
    let subArgsCollection = undefined;
    if (!isPrimitiveType(parameter.type) && parameter.type !== 'any') {
        const parameterType = (_b = getArrayType(parameter.type)) !== null && _b !== void 0 ? _b : parameter.type;
        let typeDefinition;
        try {
            typeDefinition = yield resourceRegistry.getResource(parameterType);
        }
        catch (error) { }
        if (!!typeDefinition && ((_c = typeDefinition.properties) === null || _c === void 0 ? void 0 : _c.length) > 0) {
            subArgsCollection = yield constructArgumentCollection(resourceRegistry, typeDefinition.properties, undefined, HandleType.ActionArgument, handle);
        }
    }
    return Object.assign(Object.assign({ handle, type: parameter.type }, parameter), { required: (_d = parameter.required) !== null && _d !== void 0 ? _d : true, value,
        errors,
        subArgsCollection, getErrorAggregate: (scope, severity) => getArgumentErrorAggregate(value, errors, scope, severity) });
});
export const getArgumentErrorAggregate = (argumentValue, errors, scope, severity) => {
    var _a, _b;
    let suberrors = [];
    if (Array.isArray(argumentValue)) {
        argumentValue.map(i => {
            var _a, _b;
            return (suberrors = suberrors.concat((_b = (_a = i === null || i === void 0 ? void 0 : i.getErrorAggregate) === null || _a === void 0 ? void 0 : _a.call(i, scope, severity)) !== null && _b !== void 0 ? _b : []));
        });
    }
    else {
        suberrors =
            (_b = (_a = argumentValue === null || argumentValue === void 0 ? void 0 : argumentValue.getErrorAggregate) === null || _a === void 0 ? void 0 : _a.call(argumentValue, scope, severity)) !== null && _b !== void 0 ? _b : [];
    }
    return filterErrors(scope, severity, errors).concat(suberrors);
};
export const convertValueToExpression = (handle, resourceRegistry, value, parameterName, parameterType, errors) => __awaiter(void 0, void 0, void 0, function* () {
    var _e;
    if (!value && parameterType !== PrimitiveTypes.boolean) {
        return undefined;
    }
    const arrayType = getArrayType(parameterType);
    if (arrayType) {
        if (!Array.isArray(value)) {
            errors.push(new DominoBuilderError(DominoBuilderErrorType.InvalidArrayArgument, DominoBuilderErrorScope.DESIGN, DominoBuilderErrorSeverity.ERROR, t('errors.array_type_expected'), {
                path: `${handle.type}::${parameterName}`,
                handle,
            }));
            return [];
        }
        else {
            const result = yield Promise.all(value.map((item) => __awaiter(void 0, void 0, void 0, function* () {
                return yield convertValueToExpression(handle, resourceRegistry, item, parameterName, arrayType, errors);
            })));
            return result;
        }
    }
    else {
        if (Array.isArray(value)) {
            errors.push(new DominoBuilderError(DominoBuilderErrorType.InvalidArrayArgument, DominoBuilderErrorScope.DESIGN, DominoBuilderErrorSeverity.ERROR, t('errors.unexpected_array_type'), {
                path: `${handle.type}::${parameterName}`,
                handle,
            }));
        }
        else {
            // attempt deep resolution of expressions for complex entities
            // we may or may not want this long-term ?? ::thinky-face::
            // ---
            // also, this should probably do full reconciliation of all properties specified in the entity definition,
            // marking missing ones as such -- right now it only reads in what is actually provided in the flow def
            if (!isPrimitiveType(parameterType) &&
                parameterType !== 'any' &&
                typeof value === 'object' &&
                !!value) {
                let typeDefinition;
                try {
                    typeDefinition = yield resourceRegistry.getResource(parameterType);
                }
                catch (error) { }
                if (!!typeDefinition && ((_e = typeDefinition.properties) === null || _e === void 0 ? void 0 : _e.length) > 0) {
                    let returnObj = {};
                    yield Promise.all(Object.keys(value).map((k) => __awaiter(void 0, void 0, void 0, function* () {
                        var _f;
                        const property = (_f = typeDefinition.properties) === null || _f === void 0 ? void 0 : _f.find(p => p.name === k);
                        if (!!property) {
                            returnObj[k] = yield convertValueToExpression(handle, resourceRegistry, value[k], parameterName, property.type, errors);
                        }
                    })));
                    return returnObj;
                }
            }
            return new DominoBuilderExpression(parameterType, value);
        }
    }
});
export const deconstructArgumentCollection = (argCollection, referencedActionIds) => {
    var _a;
    const nvps = (_a = argCollection === null || argCollection === void 0 ? void 0 : argCollection.args.map(a => deconstructArgument(a, referencedActionIds))) !== null && _a !== void 0 ? _a : [];
    // translate argument format if necessary
    if ((argCollection === null || argCollection === void 0 ? void 0 : argCollection.originalFormat) === 'name-value') {
        return nvps;
    }
    else {
        // originalFormat === 'object'
        return nvpToObject(nvps);
    }
};
const deconstructArgument = (arg, referencedActionIds) => {
    return {
        name: arg.name,
        value: deconstructArgumentValue(arg.type, arg.value, referencedActionIds),
    };
};
export const deconstructArgumentValue = (_type, value, referencedActionIds, retainErrors) => {
    let result;
    if (Array.isArray(value)) {
        result = value.map(val => {
            var _a;
            return deconstructArgumentValue((_a = val === null || val === void 0 ? void 0 : val._expectedType) !== null && _a !== void 0 ? _a : 'UNKNOWN', val, referencedActionIds, retainErrors);
        });
    }
    else if (typeof value === 'object') {
        // value will either be an object with properties that may be expressions OR it will be an expression (if no entity def found)
        if (value instanceof DominoBuilderExpression) {
            const expr = value;
            collectActionReferences(expr, referencedActionIds);
            result = expr === null || expr === void 0 ? void 0 : expr.deconstruct(false, retainErrors);
        }
        else {
            result = {};
            Object.keys(value).map((k) => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                result[k] = deconstructArgumentValue((_a = value[k]) === null || _a === void 0 ? void 0 : _a._expectedType, value[k], referencedActionIds, retainErrors);
            }));
        }
    }
    return result;
};
const collectActionReferences = (expr, referencedActionIds) => {
    var _a;
    (_a = expr === null || expr === void 0 ? void 0 : expr.members) === null || _a === void 0 ? void 0 : _a.forEach(m => {
        var _a;
        if (m.type === 'expression') {
            collectActionReferences(m.value, referencedActionIds);
        }
        else if (m.type === 'symbol') {
            // trigger input symbols will not have an actionHandle
            ((_a = m.value.actionHandle) === null || _a === void 0 ? void 0 : _a.id) && referencedActionIds.push(m.value.actionHandle.id);
        }
    });
};
export const isArrayArgumentRepresentation = (args) => {
    let r = true;
    if (Array.isArray(args)) {
        args.forEach(p => {
            const k = Object.keys(p);
            if (k.length !== 2 ||
                !k.some(prop => prop === 'name') ||
                !k.some(prop => prop === 'value')) {
                r = false;
            }
        });
    }
    else {
        r = false;
    }
    return r;
};
export const getClosestRelatedActionArgumentHandle = (handle) => {
    if (handle.type === HandleType.ActionArgument) {
        return handle;
    }
    else {
        return !!handle.parent
            ? getClosestRelatedActionArgumentHandle(handle.parent)
            : null;
    }
};
export const findArgument = (handle, argsCollection) => {
    if (!!!argsCollection) {
        return undefined;
    }
    let matchingArgument = argsCollection.args.find(a => a.handle.id === handle.id);
    if (matchingArgument) {
        return matchingArgument;
    }
    for (let arg of argsCollection.args) {
        matchingArgument = findArgument(handle, arg.subArgsCollection);
        if (matchingArgument) {
            return matchingArgument;
        }
    }
    return undefined;
};
export const toArgumentCollection = (parameters, parentHandle, format) => {
    return {
        args: parameters.map(parameter => ({
            name: parameter.name,
            label: parameter.label,
            description: parameter.description,
            defaultValue: parameter.defaultValue,
            type: parameter.type,
            typeArgs: parameter.typeArgs,
            required: parameter.required,
            handle: {
                id: uuid(),
                type: parentHandle.type,
                parent: parentHandle,
            },
            value: undefined,
            displayType: parameter === null || parameter === void 0 ? void 0 : parameter.displayType,
        })),
        originalFormat: format,
    };
};
//# sourceMappingURL=arguments.js.map